import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Reklaim deltas vs the v4 shadcn registry (preserved on REK-1563 Stack 2 refresh):
 *
 * - `DialogContent` exposes `hideCloseButton?: boolean` (REK-1546) -- needed by
 *   `ResponsiveWizardShell` which suppresses the default close X in favor of an
 *   in-content custom close. The v4 registry hard-codes the close X with no opt-out.
 * - `DialogContent` exposes `overlayClassName?: string` (REK-1559) -- lets callers
 *   override the overlay tint per-instance (e.g., the LemonadeProfileWizard uses
 *   `bg-background/95 backdrop-blur-sm` while standard dialogs keep `bg-black/80`).
 *   The v4 registry hard-codes the overlay class with no opt-out.
 *
 * Do NOT replace this file with `npx shadcn add dialog --overwrite` -- it would
 * remove both extension points and break `ResponsiveWizardShell` plus every wizard
 * + sheet that depends on it.
 */
const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * When `true`, suppresses the built-in top-right `<X>` close button. Use
   * this for screens that render their own close affordance (e.g. wizards
   * with a header close button that doubles as a back arrow).
   *
   * Defaults to `false` so existing callers are unaffected.
   */
  hideCloseButton?: boolean;
  /**
   * Optional className applied to the full-viewport `<DialogOverlay>` (the
   * dark backdrop behind the content). Use this to customize the backdrop
   * for screens that need a specific overlay visual (e.g. the campaign
   * wizard's `bg-background/95 backdrop-blur-sm` looks). Defaults to the
   * standard `bg-black/80` overlay.
   *
   * Note: this prop already exists on `SheetContent`; mirrored here so the
   * `ResponsiveWizardShell` can plumb the same value to both variants.
   */
  overlayClassName?: string;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideCloseButton = false, overlayClassName, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed z-50 w-full bg-background shadow-lg duration-200 overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "bottom-0 inset-x-0 max-h-[85vh] rounded-t-2xl data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        "sm:inset-x-auto sm:bottom-auto sm:left-[50%] sm:top-[50%] sm:max-w-lg sm:max-h-[90vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-sm sm:border sm:shadow-sm data-[state=closed]:sm:slide-out-to-left-1/2 data-[state=closed]:sm:slide-out-to-top-[48%] data-[state=open]:sm:slide-in-from-left-1/2 data-[state=open]:sm:slide-in-from-top-[48%] data-[state=closed]:sm:zoom-out-95 data-[state=open]:sm:zoom-in-95",
        className,
      )}
      {...props}
    >
      <DialogScrollArea>
        {children}
      </DialogScrollArea>
      {!hideCloseButton && (
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogScrollArea = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  // `grid-cols-1` constrains the implicit grid column to `minmax(0, 1fr)`
  // instead of the default `auto` (max-content). Without it, a grid item
  // whose max-content exceeds the dialog width (e.g. a long taxonomy list,
  // a wide input, or a `flex justify-between` footer) expands the column
  // past the container, and `overflow-x-hidden` clips the right side —
  // hiding footer action buttons like the campaign creator's Next (REK-1939).
  // `minmax(0, 1fr)` lets the column shrink to the container width so
  // children wrap/scroll vertically instead of overflowing horizontally.
  // `data-testid="dialog-scroll-area"` is a stable hook for E2E tests that
  // need to verify scroll-overflow behavior without coupling to Tailwind
  // class strings.
  <div data-testid="dialog-scroll-area" className={cn("overflow-y-auto overflow-x-hidden max-h-[85vh] sm:max-h-[90vh] grid grid-cols-1 gap-4 p-6", className)} {...props} />
);
DialogScrollArea.displayName = "DialogScrollArea";

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
